import { useEffect, useRef, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { ChevronLeft, Plus } from 'lucide-react';
import { cn } from '@/lib/format';
import { Badge } from './ui';

/**
 * 앱 셸 — 명세서 §7.5 정보 구조
 *
 * 학부모 탭: 홈 / 알림장 / 앨범 / 돋보기(발달) / 전체
 * 교사 탭:   홈 / 기록 / 소통 / 전체  (+ 우측 하단 FAB "빠른 작성")
 *
 * §7.1 가드: 기능 아이콘 바둑판 그리드 홈을 만들지 않는다. 홈은 카드 피드다.
 */

export function AppBar({
  title,
  subtitle,
  back,
  right,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  back?: boolean;
  right?: ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-20 border-b border-line bg-bg/90 backdrop-blur">
      <div className="flex min-h-touch items-center gap-1 px-2 py-2">
        {back && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="뒤로"
            className="tap grid place-items-center rounded-full text-ink hover:bg-primary-soft"
          >
            <ChevronLeft size={22} />
          </button>
        )}
        <div className={cn('min-w-0 flex-1', !back && 'pl-2')}>
          <h1 className="truncate text-lg font-bold">{title}</h1>
          {subtitle && <p className="truncate text-sm text-ink-sub">{subtitle}</p>}
        </div>
        {right}
      </div>
    </header>
  );
}

export function Screen({
  children,
  className,
  withNav = true,
}: {
  children: ReactNode;
  className?: string;
  withNav?: boolean;
}) {
  return (
    <main className={cn('mx-auto w-full max-w-screen-sm', withNav && 'pb-24', className)}>
      {children}
    </main>
  );
}

export interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  badge?: number;
  end?: boolean;
}

/**
 * 탭 셸 프레임 — 콘텐츠가 내부에서 스크롤되고 내비는 일반 플렉스 푸터다.
 *
 * `position: fixed` 하단바는 iOS Safari가 주소창을 접고 펼 때 화면 중간에
 * 떠오르며 그 아래로 페이지 내용이 비쳐 보였다(전체 메뉴 푸터 깨짐).
 * 앱을 자체 스크롤 컨테이너로 만들면 내비가 항상 진짜 화면 바닥에 붙는다.
 * FAB도 fixed 대신 스크롤 영역 위에 absolute로 띄운다.
 */
export function TabFrame({
  children,
  fab,
  nav,
}: {
  children: ReactNode;
  fab?: ReactNode;
  nav: ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigationType = useNavigationType();

  // 새 화면으로 들어가면 맨 위에서 시작하고, 뒤로 돌아오면 머물던 위치를 복원한다.
  // 앱이 자체 스크롤 컨테이너라 브라우저가 복원해 주지 않아 직접 기억한다.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const key = `scroll:${location.pathname}${location.search}`;

    if (navigationType === 'POP') {
      const saved = Number(sessionStorage.getItem(key) ?? 0);
      if (saved > 0) {
        // 목록 데이터가 늦게 그려지면 높이가 모자라 못 내려간다 — 잠깐 재시도한다.
        let attempts = 0;
        const restore = () => {
          container.scrollTop = saved;
          attempts += 1;
          if (Math.abs(container.scrollTop - saved) > 4 && attempts < 12) {
            window.setTimeout(restore, 80);
          }
        };
        restore();
      } else {
        container.scrollTop = 0;
      }
    } else {
      container.scrollTop = 0;
    }

    const onScroll = () => sessionStorage.setItem(key, String(container.scrollTop));
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [location.pathname, location.search, navigationType]);

  return (
    <div className="app-shell flex w-full flex-col">
      <div className="relative min-h-0 flex-1">
        <div ref={scrollRef} className="h-full overflow-y-auto">
          {children}
        </div>
        {fab}
      </div>
      {nav}
    </div>
  );
}

export function BottomNav({ items }: { items: NavItem[] }) {
  return (
    // 반투명 배경은 스크롤 위치에 따라 뒤 카드가 비쳐 지저분해 보인다.
    // 미니멀 원칙(§7.1)에 맞춰 불투명 표면 + 얇은 경계선으로 둔다.
    <nav className="border-t border-line bg-surface">
      <ul className="safe-bottom mx-auto flex max-w-screen-sm items-stretch justify-around pt-1">
        {items.map((item) => (
          <li key={item.to} className="flex-1">
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'tap relative mx-auto flex flex-col items-center justify-center gap-0.5 rounded-sm px-2 py-1 text-[11px] font-medium transition-colors',
                  // 활성 색은 브랜드 그린 — 블루 계열 금지 (§7.1)
                  isActive ? 'text-primary-strong' : 'text-ink-sub',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      'grid h-7 w-12 place-items-center rounded-full transition-colors',
                      isActive && 'bg-primary-soft',
                    )}
                  >
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                  {item.badge ? (
                    <span className="absolute right-2 top-0">
                      <Badge count={item.badge} />
                    </span>
                  ) : null}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** 교사 화면 우측 하단 "빠른 작성" (§7.5) — TabFrame의 fab 슬롯에 넣는다 */
export function Fab({ onClick, label = '빠른 작성' }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="absolute bottom-4 right-4 z-10 flex h-14 items-center gap-2 rounded-full bg-primary px-5 text-white shadow-raised transition-transform active:scale-95"
    >
      <Plus size={20} />
      <span className="text-base font-semibold">{label}</span>
    </button>
  );
}

/** 가로 스크롤 칩 줄 — 자녀 전환·필터에 쓴다 */
export function ChipRow({ children }: { children: ReactNode }) {
  return (
    <div className="scroll-x -mx-4 px-4">
      <div className="flex w-max gap-2 pb-1">{children}</div>
    </div>
  );
}
